import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { CompactBand, BandTab, BandButton } from '@/components/CompactBand';
import './elva-work.css';
import type { LogKindFilter, Period } from './types';
import { downloadLogCsv, getLogEntriesFlat, getReport } from './store';
import { money } from './utils';
import { Report } from './components/Report';
import { Log } from './components/Log';

const KIND_FROM_REPORT: Record<string, LogKindFilter> = {
  claims_sent: 'claims', calls: 'calls', posted: 'posted', eob_claims: 'posted', portal: 'portal',
  appeals: 'appeals', denials: 'all', preauths: 'all', bank_diffs: 'all',
};

// The report's period maps to the Log's date-range preset, so "See in log"
// opens the Log filtered to that kind AND period (rcm-03 Fix-on-staging).
const RANGE_FROM_PERIOD: Record<Period, string> = {
  '2026-10': 'this-month', '2026-09': 'last-month', since: 'all-time',
};

export function ElvaWork({ view }: { view: 'report' | 'log' }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const [params, setParams] = useSearchParams();

  const period = (params.get('period') ?? '2026-10') as Period;
  const report = getReport(period);
  const entries = getLogEntriesFlat();

  const leadPhrase =
    period === '2026-10' ? 'This month' : period === '2026-09' ? 'In September' : 'Since it started,';

  const periodSelect = (
    <label>
      <span style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' }}>Period</span>
      <select
        className="cbd-csel"
        value={period}
        onChange={(e) => { const n = new URLSearchParams(params); n.set('period', e.target.value); setParams(n, { replace: true }); }}
      >
        <option value="2026-10">October 2026, so far</option>
        <option value="2026-09">September 2026</option>
        <option value="since">Since ELVA started</option>
      </select>
    </label>
  );

  const header = (
    <CompactBand
      mint
      headline={
        report.tasks === 0 ? (
          <>ELVA hasn&rsquo;t done any work yet this month.</>
        ) : (
          <>
            {leadPhrase} ELVA did {report.hours} hours of your team&rsquo;s work and{' '}
            <em>found {money(report.moneyFound)}</em>.
          </>
        )
      }
      tabs={
        <>
          <BandTab label="Report" active={view === 'report'} to="/elva-work" />
          <BandTab label="Log" meta={report.tasks} active={view === 'log'} to="/elva-work/log" />
        </>
      }
      tabsRight={
        view === 'report' ? (
          <>
            {periodSelect}
            <BandButton variant="w" onClick={() => { window.open('/elva-work/report/print', '_blank'); }}>Print report</BandButton>
          </>
        ) : (
          <BandButton variant="w" onClick={() => { downloadLogCsv(entries); }}>Download the log (CSV)</BandButton>
        )
      }
    />
  );

  return (
    <div className="ew">
      {header}
      <div className="ew-body" style={view === 'log' ? { display: 'flex', flexDirection: 'column' } : undefined}>
        {view === 'report' ? (
          <Report
            report={report}
            onSeeInLog={(kind) => { void navigate(`/elva-work/log?kind=${KIND_FROM_REPORT[kind] ?? 'all'}&range=${RANGE_FROM_PERIOD[period]}`); }}
            onCorrections={() => { void navigate(`/elva-work/log?by=person&range=${RANGE_FROM_PERIOD[period]}`); }}
          />
        ) : (
          <Log
            entries={entries}
            {...(id ? { selectedId: id } : {})}
            onSelect={(eid) => { void navigate(`/elva-work/log/${eid}${params.toString() ? `?${params.toString()}` : ''}`); }}
            onClosePanel={() => { void navigate(`/elva-work/log${params.toString() ? `?${params.toString()}` : ''}`); }}
          />
        )}
      </div>
    </div>
  );
}
